/**
 * analytics.js — Guzz India · GA4 Custom Event Foundation
 *
 * Phase 0: Core trackEvent wrapper only.
 * Phase 1+: Event listeners will be added here incrementally.
 *
 * Usage:
 *   window.guzzTrack('event_name', { param: 'value' });
 *
 * Debug mode — append ?guzz_debug to any page URL:
 *   https://guzzindia.com/products/xyz?guzz_debug
 *   Every tracked event will appear in the browser console.
 */

(() => {
  const DEBUG = new URLSearchParams(window.location.search).has('guzz_debug');

  // ─── Common params appended to every event ───────────────────────────────

  function getCommonParams() {
    const d = window.guzzAnalyticsData || {};
    const page     = d.page     || {};
    const cart     = d.cart     || {};
    const customer = d.customer || {};

    let customer_type = 'guest';
    if (customer.logged_in) {
      customer_type = customer.orders_count > 0 ? 'returning' : 'new_registered';
    }

    return {
      page_type:     page.type     || '',
      page_template: page.template || '',
      currency:      cart.currency || 'INR',
      customer_type,
    };
  }

  // ─── Core tracking function ───────────────────────────────────────────────

  function guzzTrack(eventName, params = {}) {
    const payload = { ...getCommonParams(), ...params };
    if (DEBUG) payload.debug_mode = true;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event:            'guzz_event',   // GTM trigger listens for this
      guzz_event_name:  eventName,      // GA4 event name (read by GTM variable)
      ...payload,                        // all custom params flat in dataLayer
    });

    if (DEBUG) {
      console.groupCollapsed(`%c[Guzz Analytics] ${eventName}`, 'color:#2e7d32;font-weight:bold');
      console.table({ guzz_event_name: eventName, ...payload });
      console.groupEnd();
    }
  }

  // ─── Expose globally ─────────────────────────────────────────────────────

  window.guzzTrack = guzzTrack;

  // ─── Debug banner ─────────────────────────────────────────────────────────

  if (DEBUG) {
    console.log(
      '%c[Guzz Analytics] Foundation loaded ✓',
      'background:#2e7d32;color:#fff;padding:2px 8px;border-radius:3px;font-weight:bold'
    );
    console.log('Page data →', window.guzzAnalyticsData);
    console.log('Test: window.guzzTrack("test_event", { source: "console" })');
  }

  // ─── Phase 1a: pdp_viewed ────────────────────────────────────────────────
  //
  // Fires once when a product page finishes loading.
  // Data comes from window.guzzAnalyticsData (set by analytics-data.liquid).
  // page_load_ms = time from browser navigation start → deferred scripts run.

  function initPdpViewed() {
    const d = window.guzzAnalyticsData || {};
    if ((d.page || {}).type !== 'product' || !d.product) return;

    const p = d.product;

    guzzTrack('pdp_viewed', {
      product_id:        p.id,
      product_name:      p.name,
      product_handle:    p.handle,
      product_vendor:    p.vendor          || '',
      product_type:      p.type            || '',
      product_sku:       p.sku             || '',
      variant_id:        p.variant_id,
      variant_title:     p.variant_title   || '',
      price:             p.price,
      compare_at_price:  p.compare_at_price || null,
      in_stock:          p.available,
      page_load_ms:      Math.round(performance.now()),
    });
  }

  // ─── Phase 1b: add_to_cart_clicked ───────────────────────────────────────
  //
  // Subscribes to the internal PUB_SUB_EVENTS.cartUpdate bus.
  // Fires whenever any add-to-cart succeeds (PDP, quick-add, quick-order-list).
  // Skips cart quantity-update events (source === 'cart-items').
  //
  // cartData shape (from Shopify /cart/add.js response):
  //   product_title, variant_title, sku, price (paise), quantity,
  //   vendor, product_type, handle, variant_id

  function initAddToCart() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || !event.cartData || event.source === 'cart-items') return;

      const item  = event.cartData;
      const price = typeof item.price === 'number'
        ? +(item.price / 100).toFixed(2)
        : null;

      guzzTrack('add_to_cart_clicked', {
        product_name:    item.product_title  || item.title || '',
        variant_title:   item.variant_title  || '',
        product_sku:     item.sku            || '',
        variant_id:      item.variant_id     || event.productVariantId || '',
        product_handle:  item.handle         || '',
        product_vendor:  item.vendor         || '',
        product_type:    item.product_type   || '',
        price:           price,
        quantity:        item.quantity       || 1,
        line_total:      (price && item.quantity)
                           ? +(price * item.quantity).toFixed(2)
                           : null,
        add_source:      event.source        || '',
      });
    });
  }

  // ─── Phase 2: DOM Click Events ───────────────────────────────────────────

  // 1 & 2 & 3 — hamburger_menu_clicked / side_menu_opened / side_menu_closed
  function initMenuTracking() {
    const details = document.getElementById('Details-menu-drawer-container');
    if (!details) return;

    const summaryBtn = details.querySelector('summary');
    let menuOpenedAt = null;

    if (summaryBtn) {
      summaryBtn.addEventListener('click', () => {
        guzzTrack('hamburger_menu_clicked', {
          menu_state: details.open ? 'closing' : 'opening',
        });
      });
    }

    details.addEventListener('toggle', () => {
      if (details.open) {
        menuOpenedAt = performance.now();
        guzzTrack('side_menu_opened', {
          menu_items_count: details.querySelectorAll('.menu-drawer__menu-item').length,
          menu_type:        'drawer',
        });
      } else {
        guzzTrack('side_menu_closed', {
          time_spent_ms: menuOpenedAt ? Math.round(performance.now() - menuOpenedAt) : null,
          menu_type:     'drawer',
        });
        menuOpenedAt = null;
      }
    });
  }

  // 4, 5, 6, 7 — category_navigation_started / men_perfume / women_perfume / shopall
  function initNavigationTracking() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[id^="HeaderMenu-"], a[id^="HeaderDrawer-"]');
      if (!link) return;

      const linkText  = link.textContent.trim();
      const linkHref  = link.getAttribute('href') || '';
      const navType   = link.id.startsWith('HeaderDrawer') ? 'mobile_drawer' : 'desktop';
      const hrefLower = linkHref.toLowerCase();
      const textLower = linkText.toLowerCase();

      guzzTrack('category_navigation_started', {
        category_name: linkText,
        destination:   linkHref,
        nav_type:      navType,
      });

      // Specific category shortcuts — href is most reliable, text is fallback
      const isMen    = /\/collections\/men(\/|$|\?|#)/.test(hrefLower) || textLower === 'men';
      const isWomen  = hrefLower.includes('/women') || textLower === 'women';
      const isShopAll = hrefLower.includes('/all') ||
                        textLower === 'shop all' ||
                        textLower === 'unisex';

      if (isWomen) {
        guzzTrack('women_perfume_clicked', { click_source: navType, destination: linkHref });
      } else if (isMen) {
        guzzTrack('men_perfume_clicked', { click_source: navType, destination: linkHref });
      } else if (isShopAll) {
        guzzTrack('shopall_clicked', { click_source: navType, destination: linkHref });
      }
    });
  }

  // 8 — login_clicked
  function initAccountTracking() {
    document.addEventListener('click', (e) => {
      const loginLink = e.target.closest('a.header__icon--account, a.menu-drawer__account');
      if (!loginLink) return;

      const d = window.guzzAnalyticsData || {};
      guzzTrack('login_clicked', {
        button_location: loginLink.classList.contains('menu-drawer__account') ? 'mobile_drawer' : 'header',
        destination:     loginLink.getAttribute('href') || '',
        user_status:     (d.customer || {}).logged_in ? 'logged_in' : 'guest',
      });
    });
  }

  // 9 — share_clicked
  function initShareTracking() {
    document.addEventListener('click', (e) => {
      if (!e.target.closest('share-button')) return;
      const btn = e.target.closest('summary, button');
      if (!btn) return;

      const p = (window.guzzAnalyticsData || {}).product || {};
      guzzTrack('share_clicked', {
        product_name:   p.name   || '',
        product_handle: p.handle || '',
        product_sku:    p.sku    || '',
        share_type:     'web_share',
      });
    });
  }

  // 10 — prepaid_offer_clicked
  function initPrepaidTracking() {
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.mini-cart-header-offer')) return;

      const d    = window.guzzAnalyticsData || {};
      const shop = d.shop || {};
      const cart = d.cart || {};
      guzzTrack('prepaid_offer_clicked', {
        offer_type:      'prepaid_discount',
        discount_value:  shop.prepaid_discount || '',
        offer_location:  'cart_drawer',
        cart_total:      cart.total_price || null,
      });
    });
  }

  // 11 — quantity_changed
  function initQuantityTracking() {
    document.addEventListener('change', (e) => {
      const input = e.target.closest('input.quantity__input');
      if (!input) return;

      const newQty = parseInt(input.value)                          || 0;
      const oldQty = parseInt(input.dataset.cartQuantity)
                  || parseInt(input.getAttribute('value'))
                  || 1;
      const isCart = !!input.closest('quantity-input.cart-quantity');
      const p      = (window.guzzAnalyticsData || {}).product || {};

      guzzTrack('quantity_changed', {
        new_quantity: newQty,
        old_quantity: oldQty,
        direction:    newQty > oldQty ? 'increase' : 'decrease',
        location:     isCart ? 'cart' : 'pdp',
        product_name: p.name || '',
        product_sku:  p.sku  || '',
      });
    });
  }

  // 12 — reviews_clicked
  function initReviewsTracking() {
    document.addEventListener('click', (e) => {
      const reviewEl = e.target.closest(
        '.jdgm-widget, .jdgm-preview-badge, [class*="jdgm"], .rating-wrapper'
      );
      if (!reviewEl) return;

      const p = (window.guzzAnalyticsData || {}).product || {};
      guzzTrack('reviews_clicked', {
        product_name:   p.name   || '',
        product_handle: p.handle || '',
        product_sku:    p.sku    || '',
        click_source:   reviewEl.classList.contains('rating-wrapper') ? 'native_stars' : 'judgeme',
      });
    });
  }

  // ─── Phase 3: Viewport / Intersection Observer Events ───────────────────

  // Fires callback once when element enters viewport at `threshold` visibility
  function observeOnce(selector, threshold, callback) {
    const elements = document.querySelectorAll(selector);
    if (!elements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        callback(entry.target);
      });
    }, { threshold });

    elements.forEach(el => observer.observe(el));
  }

  // banner_viewed + lifestyle_banner_viewed (homepage sections)
  // note_card_viewed + notes_section_viewed (PDP sections)
  function initViewportTracking() {
    const d = window.guzzAnalyticsData || {};
    const p = d.product || {};

    // Hero carousel — homepage
    observeOnce('.unique-carousel', 0.5, () => {
      guzzTrack('banner_viewed', {
        banner_name: 'hero_carousel',
        banner_type: 'slideshow',
      });
    });

    // Men/Women lifestyle section — homepage
    observeOnce('.collection-section', 0.5, () => {
      guzzTrack('lifestyle_banner_viewed', {
        banner_name: 'men_women_collection',
        banner_type: 'collection_split',
      });
    });

    // Fragrance notes section — PDP
    observeOnce('.perfume-notes-section', 0.3, () => {
      guzzTrack('notes_section_viewed', {
        product_name:   p.name   || '',
        product_handle: p.handle || '',
        product_sku:    p.sku    || '',
      });
    });

    // Individual note cards — PDP (fires separately per card)
    observeOnce('.perfume-note', 0.5, (el) => {
      const noteName = (el.querySelector('h3') || {}).textContent || '';
      guzzTrack('note_card_viewed', {
        note_name:      noteName.trim(),
        product_name:   p.name   || '',
        product_handle: p.handle || '',
        product_sku:    p.sku    || '',
      });
    });
  }

  // scroll_depth — 25 / 50 / 75 / 90 milestones, each fires once
  function initScrollDepth() {
    const milestones = [25, 50, 75, 90];
    const fired = new Set();

    function getScrollPercent() {
      const el  = document.documentElement;
      const top = el.scrollTop || document.body.scrollTop;
      const max = el.scrollHeight - el.clientHeight;
      return max > 0 ? Math.round((top / max) * 100) : 0;
    }

    function onScroll() {
      const pct = getScrollPercent();
      milestones.forEach(m => {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          guzzTrack('scroll_depth', {
            depth_percent: m,
          });
        }
      });
      // Detach once all milestones fired
      if (fired.size === milestones.length) {
        window.removeEventListener('scroll', onScroll);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ─── Phase 3 init ─────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', () => {
    initPdpViewed();
    initAddToCart();
    initMenuTracking();
    initNavigationTracking();
    initAccountTracking();
    initShareTracking();
    initPrepaidTracking();
    initQuantityTracking();
    initReviewsTracking();
    initViewportTracking();
    initScrollDepth();
  });

  // ─── Phase 4: Complex Interaction Events ────────────────────────────────

  // product_image_swiped
  // Mobile  : slider-component fires slideChanged on touch swipe
  // Desktop : AI-generated lookbook carousel (blocks/ai_gen_block_1646187.liquid)
  //           has no thumbnails — uses prev/next buttons with data-direction inside .desktop-carousel
  function initProductImageSwiped() {
    const p = (window.guzzAnalyticsData || {}).product || {};
    let swipeCount = 0;

    function trackImageChange(mediaId, method) {
      swipeCount++;
      guzzTrack('product_image_swiped', {
        product_name:      p.name   || '',
        product_handle:    p.handle || '',
        media_id:          mediaId,
        swipe_count:       swipeCount,
        navigation_method: method,
      });
    }

    // Mobile — slideChanged fires on swipe in slider-component
    const viewer = document.querySelector('slider-component[id^="GalleryViewer"]');
    if (viewer) {
      viewer.addEventListener('slideChanged', (e) => {
        trackImageChange(e.detail?.currentElement?.dataset?.mediaId || '', 'swipe');
      });
    }

    // Desktop — AI lookbook carousel arrow buttons (data-direction="prev" or "next")
    document.addEventListener('click', (e) => {
      const navBtn = e.target.closest('.desktop-carousel button[data-direction]');
      if (!navBtn) return;
      trackImageChange('', navBtn.dataset.direction === 'prev' ? 'arrow_prev' : 'arrow_next');
    });
  }

  // product_zoomed — fires when user clicks the zoom/modal opener on product image
  function initProductZoomed() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('.product__modal-opener');
      if (!opener) return;

      const p       = (window.guzzAnalyticsData || {}).product || {};
      const mediaId = opener.dataset.mediaId || '';

      guzzTrack('product_zoomed', {
        product_name:   p.name   || '',
        product_handle: p.handle || '',
        media_id:       mediaId,
        zoom_type:      'modal',
      });
    });
  }

  // note_card_clicked — fires when user clicks "DISCOVER MORE" on a note card
  function initNoteCardClicked() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.discover-text .btn');
      if (!btn) return;

      const slide = btn.closest('.discover-slide');
      if (!slide) return;

      const noteType    = (slide.querySelector('.discover-label')   || {}).textContent?.trim() || '';
      const productName = (slide.querySelector('.discover-text h3') || {}).textContent?.trim() || '';

      guzzTrack('note_card_clicked', {
        note_type:    noteType,
        product_name: productName,
        destination:  btn.getAttribute('href') || '',
        slide_index:  parseInt(slide.dataset.index) || 0,
      });
    });
  }

  // time_spent_on_section — tracks how long user views each major section
  function initTimeSpentTracking() {
    const SECTIONS = [
      { selector: '.product__info-container', name: 'product_info'         },
      { selector: '.perfume-notes-section',   name: 'fragrance_notes'      },
      { selector: '.unique-carousel',         name: 'hero_banner'          },
      { selector: '.discover-wrap',           name: 'shop_by_note'         },
      { selector: '.story-section',           name: 'our_story'            },
      { selector: '.collection-section',      name: 'men_women_collection' },
    ];

    const activeSections = new Map(); // element → enteredAt timestamp

    SECTIONS.forEach(({ selector, name }) => {
      const el = document.querySelector(selector);
      if (!el) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            activeSections.set(el, { name, enteredAt: performance.now() });
          } else {
            const session = activeSections.get(el);
            if (!session) return;
            const timeMs = Math.round(performance.now() - session.enteredAt);
            activeSections.delete(el);
            if (timeMs < 2000) return; // ignore accidental scroll-throughs
            guzzTrack('time_spent_on_section', {
              section_name:  name,
              time_spent_ms: timeMs,
              time_spent_s:  Math.round(timeMs / 1000),
            });
          }
        });
      }, { threshold: 0.3 });

      observer.observe(el);
    });

    // Fire for any still-visible sections when page is hidden/closed
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) return;
      activeSections.forEach(({ name, enteredAt }, el) => {
        const timeMs = Math.round(performance.now() - enteredAt);
        if (timeMs < 2000) return;
        guzzTrack('time_spent_on_section', {
          section_name:  name,
          time_spent_ms: timeMs,
          time_spent_s:  Math.round(timeMs / 1000),
          exit_type:     'page_hidden',
        });
      });
      activeSections.clear();
    });
  }

  // ─── Phase 4 init ─────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', () => {
    initPdpViewed();
    initAddToCart();
    initMenuTracking();
    initNavigationTracking();
    initAccountTracking();
    initShareTracking();
    initPrepaidTracking();
    initQuantityTracking();
    initReviewsTracking();
    initViewportTracking();
    initScrollDepth();
    initProductImageSwiped();
    initProductZoomed();
    initNoteCardClicked();
    initTimeSpentTracking();
  });

})();
